import {gql} from '@apollo/client';
import React from 'react';

import {Permissions} from 'auth/PermissionConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {ResultSchemaSelectorQuery} from 'results/__generated/ResultSchemaSelectorQuery';
import SchemaSelector from 'samples/SchemaSelector';
import {getSchemaSearchableMenuOptionDatas} from 'samples/SchemaStarsUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ResultSchemaGraphQL} from '../../graphql-fragments/results/schemas/__generated/ResultSchemaGraphQL';
import {ResultSchema} from './assay-schemas/AssaySchemaConstants';

export interface QueryingResultSchemaSelectorProps
  extends Omit<ResultSchemaSelectorIdInputProps, 'selectedSchemaId'> {
  selectedSchema: ResultSchema | null;
}

export default function QueryingResultSchemaSelector({
  selectedSchema,
  onChange,
  isDisabled,
  filter,
  shouldShowNoSchemaOption,
  requiredPermissions,
  renderThrobberWhileLoading,
}: QueryingResultSchemaSelectorProps): JSX.Element | null {
  return (
    <ResultSchemaSelectorIdInput
      selectedSchemaId={selectedSchema ? selectedSchema.id : null}
      isDisabled={isDisabled}
      onChange={onChange}
      filter={filter}
      shouldShowNoSchemaOption={shouldShowNoSchemaOption}
      requiredPermissions={requiredPermissions}
      renderThrobberWhileLoading={renderThrobberWhileLoading}
    />
  );
}

interface ResultSchemaSelectorIdInputProps extends Omit<ResultSchemaSelectorProps, 'schemas' | 'onChange'> {
  filter?: (schema: ResultSchema) => boolean;
  onChange: (newSelectedSchema: ResultSchema | null) => void;
  requiredPermissions?: Array<keyof Permissions>;
  /**
   * Whether to render a throbber while the result schemas are loading. Otherwise,
   * the selector will be disabled while loading. It is generally preferred not to set this to `true`.
   *
   * @default false
   */
  renderThrobberWhileLoading?: boolean;
}

export function ResultSchemaSelectorIdInput({
  selectedSchemaId,
  isDisabled,
  onChange,
  filter,
  shouldShowNoSchemaOption,
  requiredPermissions,
  renderThrobberWhileLoading,
}: ResultSchemaSelectorIdInputProps): JSX.Element | null {
  const queryResult = useBenchlingQuery<ResultSchemaSelectorQuery>(RESULT_SCHEMA_SELECTOR_QUERY);

  if (renderThrobberWhileLoading && queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return null;
  }

  const schemas: Array<ResultSchemaGraphQL> =
    queryResult.type === QueryResultType.SUCCESS ? queryResult.data.resultSchemas : [];

  let resultSchemas: Array<ResultSchema> = graphQLArrayToREST('ResultSchema', schemas).filter(
    (schema) => !requiredPermissions || requiredPermissions.every((perm) => schema.permissions[perm])
  );

  if (filter != null) {
    resultSchemas = resultSchemas.filter((schema) => filter(schema));
  }

  const selectedSchema = schemas.find((schema) => schema.id === selectedSchemaId) ?? null;

  return (
    <ResultSchemaSelector
      schemas={resultSchemas}
      selectedSchemaId={selectedSchema?.id ?? null}
      onChange={(newSelectedSchemaId) => {
        if (newSelectedSchemaId == null) {
          onChange(null);
        } else {
          const schema = schemas.find((schema) => schema.id === newSelectedSchemaId)!;
          onChange(graphQLToREST('ResultSchema', schema));
        }
      }}
      isDisabled={isDisabled || queryResult.type === QueryResultType.PENDING}
      shouldShowNoSchemaOption={shouldShowNoSchemaOption}
    />
  );
}

export const RESULT_SCHEMA_SELECTOR_QUERY = gql`
  query ResultSchemaSelectorQuery @skipAudit @engComponent(name: RESULTS) {
    resultSchemas {
      ... on ResultSchemaNode {
        ...ResultSchemaGraphQL
      }
    }
  }
  ${getFragmentWrapper('ResultSchema').fragment}
`;

export interface ResultSchemaSelectorProps {
  schemas: Array<ResultSchema>;
  selectedSchemaId: string | null;
  onChange: (newSelectedSchemaId: string | null) => void;
  isDisabled?: boolean;
  shouldShowNoSchemaOption?: boolean;
}

const DEFAULT_SHOULD_SHOW_NO_SCHEMA_OPTION = false;
export function ResultSchemaSelector({
  schemas,
  selectedSchemaId,
  onChange,
  isDisabled,
  shouldShowNoSchemaOption,
}: ResultSchemaSelectorProps): JSX.Element {
  return (
    <SchemaSelector
      isDisabled={isDisabled}
      selectedSchemaId={selectedSchemaId}
      onChange={(selectedSchema: {id: string; name: string} | null) => {
        onChange(selectedSchema?.id ?? null);
      }}
      schemas={schemas}
      menuOptionDatas={getSchemaSearchableMenuOptionDatas<ResultSchema>(schemas, [], {
        shouldShowNoSchemaOption: shouldShowNoSchemaOption ?? DEFAULT_SHOULD_SHOW_NO_SCHEMA_OPTION,
        getSchemaOwner: (schema: ResultSchema) => schema.organization.name,
      })}
    />
  );
}
